// One card's epic controls (#6753; prototype epicControllable `:2338`, epicCtl `:2339`,
// modelPick `:2346`, the epic Stop confirm `:2368`, epicAct `:2375`): Pause, Resume and
// Stop on an epic, and its model picker, over #6751's plan pause doors, the plan cancel and
// #7012's plan model door. Built on the card's controller (`./card-controls.ts`): the same
// page guard, refusals, Stop note and live region.
//
// AN EPIC IS NAMED BY ITS TITLE in every name, string and announcement: the wire carries no
// epic number (`plan_id` is a minted string; brief item 110).
//
// WHICH EPICS. Controls show on an epic with a running slice or a pause mark, on a machine
// that is Autonomous or Needs you (prototype epicControllable, `paused_at` standing for its
// `e.upaused`). Pausing an epic holds its running AND its waiting slices (owner ruling
// 2026-10-20), so a fully paused epic reads `running: 0`: its `paused_at` is what keeps its
// Resume across a re-read.

import type { TaskLlmPreference } from '@developerz/domain';
import { createEffect, createSignal, untrack } from 'solid-js';
import { t, tPlural } from '../i18n';
import { ApiError } from './api';
import { type CardControls, controlKey, type PauseValue, type SkipLine } from './card-controls';
import { epicModelName, epicModelValue, epicPinOf } from './epic-model';
import { STOP_UNDO_MS } from './flood-guard';
import { wantedValue } from './flood-guard-context';
import {
  ANYWHERE_REASONS,
  pausePlan,
  refusalText,
  resumePlan,
  type SkippedJob,
  setPlanModel,
} from './job-controls';
import type { CardEpic, CardState } from './machine-card';
import { type Bridged, bridge, settleBridges } from './override-bridge';
import { cancelPlan } from './plan';
import { planStopMessage } from './planStopMessage';

/** How long "From the next slice" shows after a model change lands (prototype `:2706`). */
export const MODEL_NOTE_MS = 4_000;

/** The epic's controllable machine states (prototype epicControllable). */
const CONTROLLABLE_STATES: ReadonlySet<CardState> = new Set<CardState>(['autonomous', 'needs']);

/** The epic half of the card's state: overrides, skipped slices, the model notes. */
function createEpicState(card: CardControls) {
  // What an answer said about each epic: its pause mark and its model, each half its own
  // bridge to the first read that started after that answer (`./override-bridge.ts`).
  const [pausedOv, setPausedOv] = createSignal<ReadonlyMap<string, Bridged<string | null>>>(
    new Map(),
  );
  const [llmOv, setLlmOv] = createSignal<ReadonlyMap<string, Bridged<TaskLlmPreference | null>>>(
    new Map(),
  );
  const [skips, setSkips] = createSignal<ReadonlyMap<string, readonly SkipLine[]>>(new Map());
  const [modelNotes, setModelNotes] = createSignal<ReadonlySet<string>>(new Set());
  // A read that started after an answer wins, whatever it shows (review thread 4436681010).
  createEffect(() => {
    card.input.box();
    const readAt = card.input.readAt();
    untrack(() => {
      const paused = settleBridges(pausedOv(), readAt);
      if (paused !== pausedOv()) setPausedOv(paused);
      const llm = settleBridges(llmOv(), readAt);
      if (llm !== llmOv()) setLlmOv(llm);
    });
  });
  const storedPaused = (e: CardEpic): string | null | undefined => {
    const o = pausedOv().get(e.plan_id);
    return o === undefined ? e.paused_at : o.value;
  };
  const stored = (e: CardEpic): PauseValue => (storedPaused(e) != null ? 'pause' : 'run');
  return {
    card,
    /** Whether an answer's pause mark stands for this epic. */
    hasPausedOverride: (id: string): boolean => pausedOv().has(id),
    skips,
    modelNotes,
    storedPaused,
    stored,
    value: (e: CardEpic): PauseValue =>
      (wantedValue(card.page, controlKey.epicPause(e.plan_id)) as PauseValue | undefined) ??
      stored(e),
    storedLlm(e: CardEpic): TaskLlmPreference | null | undefined {
      const o = llmOv().get(e.plan_id);
      return o === undefined ? e.llm : o.value;
    },
    overridePaused(id: string, pausedAt: string | null): void {
      setPausedOv((m) => new Map(m).set(id, bridge(pausedAt, Date.now())));
    },
    overrideLlm(id: string, llm: TaskLlmPreference | null): void {
      setLlmOv((m) => new Map(m).set(id, bridge(llm, Date.now())));
    },
    setSkips(id: string, lines: readonly SkipLine[] | null): void {
      setSkips((m) => {
        const next = new Map(m);
        if (lines === null) next.delete(id);
        else next.set(id, lines);
        return next;
      });
    },
    noteModel(id: string, on: boolean): void {
      setModelNotes((s) => {
        const next = new Set(s);
        if (on) next.add(id);
        else next.delete(id);
        return next;
      });
    },
  };
}

type EpicState = ReturnType<typeof createEpicState>;

const skipLines = (
  es: EpicState,
  skipped: readonly SkippedJob[],
  resume: boolean,
): readonly SkipLine[] =>
  skipped.map((s) => ({
    taskId: s.task_id,
    name: es.card.titleOf(s.task_id),
    text: refusalText(s.reason, t),
    anywhere: resume && ANYWHERE_REASONS.has(s.reason),
  }));

async function sendPause(es: EpicState, e: CardEpic, v: PauseValue): Promise<void> {
  const { card } = es;
  const title = e.title;
  try {
    if (v === 'pause') {
      const a = await pausePlan(e.plan_id, card.input.token);
      es.overridePaused(e.plan_id, a.paused_at);
      es.setSkips(e.plan_id, skipLines(es, a.skipped, false));
      const n = a.skipped.length;
      card.setLive(
        n === 0
          ? t('fleet.machines.ctl_epic_paused_live', { title })
          : tPlural('fleet.machines.ctl_epic_paused_skipped', n, { title, count: n }),
      );
    } else {
      const a = await resumePlan(e.plan_id, card.input.token);
      es.overridePaused(e.plan_id, null);
      es.setSkips(e.plan_id, skipLines(es, a.skipped, true));
      card.setLive(t('fleet.machines.ctl_epic_resumed_live', { title }));
    }
  } catch (err) {
    card.failed(err, v === 'pause' ? 'pause' : 'resume', e.plan_id);
  } finally {
    card.input.onWritten();
  }
}

async function sendStop(card: CardControls, e: CardEpic): Promise<void> {
  try {
    const result = await cancelPlan(e.plan_id, card.input.token);
    card.putNote({ kind: 'epic', id: e.plan_id, text: planStopMessage(result), undo: false });
    card.later(STOP_UNDO_MS, () => card.clearNote('epic', e.plan_id, 'answer'));
  } catch (err) {
    card.failed(err, 'plan_cancel', e.plan_id);
  } finally {
    card.input.onWritten();
  }
}

async function sendModel(es: EpicState, e: CardEpic, picked: string): Promise<void> {
  const { card } = es;
  const llm = epicPinOf(picked);
  if (llm === undefined) {
    card.refuse(e.plan_id, { text: refusalText(null, t), anywhere: false });
    return;
  }
  try {
    const a = await setPlanModel(e.plan_id, llm, card.input.token);
    es.overrideLlm(e.plan_id, a.llm);
    es.noteModel(e.plan_id, true);
    card.later(MODEL_NOTE_MS, () => es.noteModel(e.plan_id, false));
    card.setLive(
      t('fleet.machines.ctl_model_live', { title: e.title, model: epicModelName(a.llm) }),
    );
  } catch (err) {
    // A 503 is the door's live key-pool read faulting: say so, change nothing.
    if (err instanceof ApiError && err.status === 503) {
      card.refuse(e.plan_id, { text: t('fleet.machines.ctl_model_unavailable'), anywhere: false });
    } else card.failed(err, 'model', e.plan_id);
  } finally {
    card.input.onWritten();
  }
}

/**
 * The epics as the card shows them: each pause mark and model as the latest answer or a
 * waiting change says, so "Paused by you" shows at once. A field the api did not report stays
 * absent: it claims nothing.
 */
function foldEpic(es: EpicState, e: CardEpic): CardEpic {
  const reportsPause = e.paused_at !== undefined || es.hasPausedOverride(e.plan_id);
  const waiting = wantedValue(es.card.page, controlKey.epicPause(e.plan_id)) as
    | PauseValue
    | undefined;
  const paused = es.storedPaused(e) ?? null;
  const shown = waiting === undefined ? paused : waiting === 'pause' ? (paused ?? 'waiting') : null;
  const llm = es.storedLlm(e);
  return {
    ...e,
    ...(reportsPause || waiting !== undefined ? { paused_at: shown } : {}),
    ...(llm === undefined ? {} : { llm }),
  };
}

/** One card's epic controls, over the card's own controller. */
export function createEpicControls(card: CardControls) {
  const es = createEpicState(card);
  const { page } = card;
  return {
    /** Pause / Resume / Stop show on this epic, on a machine in this state. */
    controllable: (e: CardEpic, state: CardState): boolean =>
      CONTROLLABLE_STATES.has(state) && (e.running > 0 || es.storedPaused(e) != null),
    value: es.value,
    toggle(e: CardEpic): void {
      card.clearRefusal(e.plan_id);
      es.setSkips(e.plan_id, null);
      page.guard.change<PauseValue>(controlKey.epicPause(e.plan_id), {
        from: es.stored(e),
        to: es.value(e) === 'run' ? 'pause' : 'run',
        send: (v) => sendPause(es, e, v),
      });
    },
    skips: (id: string): readonly SkipLine[] => es.skips().get(id) ?? [],
    fold: (epics: readonly CardEpic[] | null): readonly CardEpic[] | null =>
      epics === null ? null : epics.map((e) => foldEpic(es, e)),
    askStop(e: CardEpic): void {
      card.askStop({ kind: 'epic', id: e.plan_id, name: e.title });
    },
    confirmStop(e: CardEpic): void {
      const a = card.takeAsk();
      if (a === null) return;
      card.queueStop(
        a,
        controlKey.epicStop(e.plan_id),
        {
          stopped: t('fleet.machines.ctl_epic_stopped', { title: e.title }),
          restored: t('fleet.machines.ctl_epic_restored', { title: e.title }),
        },
        () => sendStop(card, e),
      );
    },
    /** The picker's value: a waiting pick, else the stored model (the latest answer's first). */
    modelValue(e: CardEpic): string | null {
      const waiting = wantedValue(page, controlKey.epicModel(e.plan_id));
      if (typeof waiting === 'string') return waiting;
      const llm = es.storedLlm(e);
      return llm === undefined ? null : epicModelValue(llm);
    },
    storedLlm: es.storedLlm,
    modelNote: (id: string): boolean => es.modelNotes().has(id),
    pickModel(e: CardEpic, picked: string): void {
      card.clearRefusal(e.plan_id);
      if (card.locked() > 0) {
        card.refuse(e.plan_id, { text: t('fleet.machines.ctl_model_locked'), anywhere: false });
        return;
      }
      const llm = es.storedLlm(e);
      if (llm === undefined) return;
      page.guard.change(controlKey.epicModel(e.plan_id), {
        from: epicModelValue(llm),
        to: picked,
        send: (v) => sendModel(es, e, v),
      });
    },
  };
}

export type EpicControls = ReturnType<typeof createEpicControls>;
